/**
 * MechanicComposerOverview.js — Mini overview bar with completion pills.
 *
 * Horizontal scrollable bar showing validation status for each composer
 * section. Clicking a pill scrolls the editor to that section.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { CheckCircle2, Circle, AlertCircle, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getOverviewStatus } from '../../../utils/homebrewValidation';

const SECTIONS = [
  { key: 'basics', number: 1, label: 'Basics' },
  { key: 'actions', number: 2, label: 'Actions' },
  { key: 'tokens', number: 3, label: 'Tokens' },
  { key: 'passives', number: 4, label: 'Passives' },
  { key: 'display', number: 5, label: 'Display' },
];

/**
 * @param {object} props
 * @param {object} props.config - Current composed mechanic config
 * @param {function} props.onScrollToSection - (sectionIndex: number) => void
 */
export default function MechanicComposerOverview({ config, onScrollToSection }) {
  const status = getOverviewStatus(config);

  return (
    <div className="flex items-center gap-2 overflow-x-auto px-4 py-2 bg-gray-900/80 border-b border-gray-700 scrollbar-thin">
      {SECTIONS.map((section) => {
        const sectionStatus = status[section.key];
        return (
          <button
            key={section.key}
            type="button"
            onClick={() => onScrollToSection(section.number - 1)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border',
              sectionStatus === 'complete' && 'bg-green-900/30 border-green-700 text-green-400',
              sectionStatus === 'error' && 'bg-red-900/30 border-red-700 text-red-400',
              sectionStatus === 'incomplete' && 'bg-gray-800 border-gray-600 text-gray-400',
              sectionStatus === 'optional' && 'bg-gray-800/50 border-gray-700 text-gray-500',
            )}
          >
            {sectionStatus === 'complete' && <CheckCircle2 className="w-3.5 h-3.5" />}
            {sectionStatus === 'error' && <AlertCircle className="w-3.5 h-3.5" />}
            {sectionStatus === 'incomplete' && <Circle className="w-3.5 h-3.5" />}
            {sectionStatus === 'optional' && <Plus className="w-3.5 h-3.5" />}
            <span>{section.number}</span>
            <span>{section.label}</span>
          </button>
        );
      })}
    </div>
  );
}
